import React from "react";
import { Helmet } from "react-helmet-async";
import Header from "../header";
import Footer from "../footer";
import { Link } from "react-router-dom";
import { ChevronRight, Home, ArrowRight, Store, Zap, Shield, Gift } from "lucide-react";
import { generateBreadcrumbJsonLd } from "../utils/breadcrumbJsonLd";

type BlogLayoutProps = {
  children: React.ReactNode;
  title: string;
  description?: string;
  publishedAt?: string;
  updatedAt?: string;
  category?: string;
  breadcrumbs?: { label: string; href?: string }[];
};

const BlogLayout: React.FC<BlogLayoutProps> = ({
  children,
  title,
  description,
  publishedAt,
  updatedAt,
  category,
  breadcrumbs = [],
}) => {
  const breadcrumbItems = [
    { name: "ホーム", url: "https://www.joybase.jp/" },
    { name: "ブログ", url: "https://www.joybase.jp/blog" },
    ...breadcrumbs.map((crumb) => ({
      name: crumb.label,
      url: crumb.href ? `https://www.joybase.jp${crumb.href}` : "https://www.joybase.jp/blog"
    }))
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Helmet>
        <script type="application/ld+json">
        {generateBreadcrumbJsonLd(breadcrumbItems)}
        </script>
      </Helmet>
      <Header />

      {/* Main Content - 2 Column Layout */}
      <main className="flex-1 pt-28 pb-6 md:pt-32 md:pb-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left Column - Article Content */}
            <div className="flex-1 min-w-0">
              <article className="bg-white rounded-lg shadow-sm p-6 md:p-10">
                {/* Breadcrumbs - Inside Article */}
                <nav className="flex items-center text-sm text-gray-500 mb-6">
                  <Link to="/" className="hover:text-blue-600 flex items-center">
                    <Home className="w-4 h-4" />
                  </Link>
                  <ChevronRight className="w-4 h-4 mx-2" />
                  <Link to="/blog" className="hover:text-blue-600">
                    ブログ
                  </Link>
                  {breadcrumbs.map((crumb, index) => (
                    <React.Fragment key={index}>
                      <ChevronRight className="w-4 h-4 mx-2" />
                      {crumb.href ? (
                        <Link to={crumb.href} className="hover:text-blue-600">
                          {crumb.label}
                        </Link>
                      ) : (
                        <span className="text-gray-700">{crumb.label}</span>
                      )}
                    </React.Fragment>
                  ))}
                </nav>

                {/* Article Header - Inside Article */}
                <header className="mb-8 pb-6 border-b border-gray-200">
                  {category && (
                    <span className="inline-block bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded mb-4">
                      {category}
                    </span>
                  )}
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                    {title}
                  </h1>
                  {description && (
                    <p className="mt-3 text-base text-gray-600">{description}</p>
                  )}
                  <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                    {publishedAt && (
                      <span>公開: {publishedAt}</span>
                    )}
                    {updatedAt && (
                      <span>更新: {updatedAt}</span>
                    )}
                  </div>
                </header>
                {/* 読みやすさ重視のブログスタイル */}
                <div
                  className="
                    max-w-none
                    text-gray-800
                    text-base md:text-[17px]
                    leading-[1.9]
                    [&>p]:mb-6
                    [&>p]:text-gray-700
                    [&>h2]:text-xl md:[&>h2]:text-2xl
                    [&>h2]:font-bold
                    [&>h2]:text-gray-900
                    [&>h2]:mt-12
                    [&>h2]:mb-6
                    [&>h2]:pb-3
                    [&>h2]:border-b-2
                    [&>h2]:border-gray-200
                    [&>h3]:text-lg md:[&>h3]:text-xl
                    [&>h3]:font-bold
                    [&>h3]:text-gray-800
                    [&>h3]:mt-8
                    [&>h3]:mb-4
                    [&>h3]:bg-gray-100
                    [&>h3]:px-4
                    [&>h3]:py-2
                    [&>h3]:rounded
                    [&>ul]:mb-6
                    [&>ul]:pl-6
                    [&>ul>li]:mb-2
                    [&>ul>li]:text-gray-700
                    [&>ul>li]:list-disc
                    [&>ol]:mb-6
                    [&>ol]:pl-6
                    [&>ol>li]:mb-2
                    [&>ol>li]:text-gray-700
                    [&>ol>li]:list-decimal
                    [&_a]:text-blue-600
                    [&_a]:underline
                    [&_a]:underline-offset-2
                    hover:[&_a]:text-blue-800
                    [&_strong]:text-gray-900
                    [&_strong]:font-semibold
                    [&>table]:w-full
                    [&>table]:mb-6
                    [&>table]:border-collapse
                    [&>table_th]:bg-gray-100
                    [&>table_th]:border
                    [&>table_th]:border-gray-300
                    [&>table_th]:px-4
                    [&>table_th]:py-3
                    [&>table_th]:text-left
                    [&>table_th]:font-semibold
                    [&>table_th]:text-gray-800
                    [&>table_td]:border
                    [&>table_td]:border-gray-300
                    [&>table_td]:px-4
                    [&>table_td]:py-3
                    [&>table_td]:text-gray-700
                  "
                >
                  {children}
                </div>

                {/* Article Bottom CTA Banner */}
                <div className="mt-10 pt-8 border-t border-gray-200">
                  <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-xl p-6 md:p-8">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div>
                        <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                          JoyBaseでオリパ販売を始めませんか？
                        </h3>
                        <p className="text-gray-800 text-sm md:text-base">
                          初期費用・月額費用0円。10連ガチャ、ラストワン賞など機能も充実。
                        </p>
                      </div>
                      <a
                        href="https://dashboard.joybase.jp/signup"
                        className="inline-flex items-center justify-center px-6 py-3 bg-gray-900 text-white font-bold rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap"
                      >
                        無料で店舗登録
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </a>
                    </div>
                  </div>
                </div>
              </article>

              {/* Bottom CTA - Mobile/Tablet (when sidebar hidden) */}
              <div className="mt-8 lg:hidden bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  JoyBaseでオリパ販売を始めませんか？
                </h3>
                <p className="text-gray-800 mb-4 text-sm">
                  初期費用・月額費用0円。10連ガチャ、ラストワン賞など機能も充実。
                </p>
                <a
                  href="https://dashboard.joybase.jp/signup"
                  className="inline-flex items-center justify-center w-full px-6 py-3 bg-gray-900 text-white font-bold rounded-lg hover:bg-gray-800 transition-colors"
                >
                  無料で店舗登録
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </div>
            </div>

            {/* Right Column - Sidebar */}
            <div className="lg:w-80 flex-shrink-0">
              <div className="sticky top-8 space-y-6">
                {/* Related Articles */}
                <div className="bg-white rounded-xl p-5 shadow-sm">
                  <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-1 h-4 bg-blue-600 rounded"></span>
                    関連記事
                  </h3>
                  <div className="space-y-4">
                    <Link
                      to="/blog/oripa-site-comparison"
                      className="block group"
                    >
                      <span className="text-xs text-blue-600 font-semibold">比較</span>
                      <h4 className="mt-1 text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                        オリパサイト構築サービス比較
                      </h4>
                      <p className="mt-1 text-xs text-gray-500">
                        費用・機能を徹底比較
                      </p>
                    </Link>
                    <div className="border-t border-gray-100"></div>
                    <Link
                      to="/blog/kobutsusho-guide"
                      className="block group"
                    >
                      <span className="text-xs text-blue-600 font-semibold">ガイド</span>
                      <h4 className="mt-1 text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                        古物商許可証の取り方完全ガイド
                      </h4>
                      <p className="mt-1 text-xs text-gray-500">
                        オリパ販売に必要な許可証の取得方法
                      </p>
                    </Link>
                    <div className="border-t border-gray-100"></div>
                    <Link
                      to="/how-to-start"
                      className="block group"
                    >
                      <span className="text-xs text-blue-600 font-semibold">始め方</span>
                      <h4 className="mt-1 text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                        JoyBaseでオリパ販売を始める
                      </h4>
                      <p className="mt-1 text-xs text-gray-500">
                        店舗登録から販売開始までの手順
                      </p>
                    </Link>
                  </div>
                </div>

                {/* Store Registration CTA */}
                <div className="rounded-xl shadow-lg overflow-hidden">
                  {/* Header with accent */}
                  <div className="bg-yellow-400 px-5 py-3">
                    <div className="flex items-center gap-2 text-gray-900">
                      <Store className="w-4 h-4" />
                      <span className="text-sm font-bold">店舗オーナー様へ</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="bg-white p-5">
                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                      オリパサイトを無料で開設
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      初期費用・月額費用 <span className="font-bold text-yellow-600">0円</span>
                    </p>

                    <div className="space-y-2 mb-5 text-sm text-gray-700">
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-yellow-500" />
                        <span>10連・100連ガチャ</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Gift className="w-4 h-4 text-yellow-500" />
                        <span>ラストワン賞・キリ番賞</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-yellow-500" />
                        <span>SMS認証・年齢確認</span>
                      </div>
                    </div>

                    <a
                      href="https://dashboard.joybase.jp/signup"
                      className="flex items-center justify-center w-full px-4 py-3 bg-yellow-400 text-gray-900 font-bold rounded-lg hover:bg-yellow-500 transition-colors"
                    >
                      無料で店舗登録
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </a>
                  </div>
                </div>

                {/* Contact */}
                <div className="bg-gray-100 rounded-xl p-5">
                  <h3 className="text-sm font-bold text-gray-900 mb-2">
                    お問い合わせ
                  </h3>
                  <p className="text-xs text-gray-600 mb-3">
                    JoyBaseの導入に関するご質問はお気軽にどうぞ
                  </p>
                  <a
                    href="mailto:support@joybase.jp"
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    support@joybase.jp
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogLayout;
